import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { MainContext } from "../../controllers/main"
import { NotificationsContext } from "../../controllers/notifications"
import { registerAnalyticsEvent } from "../../services/utilities/utility"
import ListContainer from "../../components/global/listContainer"
import { LinearProgress } from "@mui/material"
import TemplatesListTopBar from "../../components/notification/templatesListTopBar"
import TableSpinner from "../../components/global/tableSpinner"
import ListTable from "../../components/global/listTable"
import NotificationTemplate from "../../models/notificationTemplate"
import CreateTemplateDialog from "../../components/notification/createTemplateDialog"
import TemplatesListRow from "../../components/notification/templatesListRow"

const TemplatesList = ({
  forDialog,
  selectedTemplate,
  setSelectedTemplate,
}: {
  forDialog?: boolean
  selectedTemplate?: NotificationTemplate
  setSelectedTemplate?: Dispatch<SetStateAction<NotificationTemplate>>
}) => {
  const { showPadding } = useContext(MainContext)
  const {
    loading,
    templatesList,
    templatesListNextToken,
    getTemplatesList,
    searchTemplatesList,
    loadMoreTemplates,
    hasSearched,
    updatingList,
    setUpdatingList,
    searchTitle,
    setSearchTitle,
    searchStage,
    searchLang,
  } = useContext(NotificationsContext)

  // list background update
  const backgroundUpdate = () => {
    if (!templatesList.length) {
      getTemplatesList()
    } else if (!loading && !hasSearched) {
      setUpdatingList(true)
      getTemplatesList(false)
    }
  } // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    backgroundUpdate()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // handle create action dialog
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)

  const handleDialogChange = useCallback(() => {
    setDialogOpen(!dialogOpen)
  }, [dialogOpen])

  // load more button loading
  const [loadMoreLoading, setLoadMoreLoading] = useState<boolean>(false)

  // register analytics event
  useEffect(() => {
    registerAnalyticsEvent({ type: "page_view", name: "templates_list" })
  }, [])

  return (
    <ListContainer style={{ position: "relative" }}>
      {updatingList && (
        <LinearProgress
          style={{
            position: "absolute",
            top: 143,
            width: "100%",
            zIndex: 10,
          }}
        />
      )}
      <TemplatesListTopBar
        forDialog={forDialog}
        loading={loading}
        textFieldValue={searchTitle}
        textFieldPlaceholder="Search Templates"
        addButtonLabel="Add Template"
        textFieldOnChange={(e) => {
          setSearchTitle(e.target.value)
          if (e.target.value.length === 0 && hasSearched) {
            setUpdatingList(true)

            if (!searchLang && !searchStage) {
              getTemplatesList(false)
            } else {
              searchTemplatesList()
            }
          }
        }}
        textFieldOnKeyDown={(e) => {
          if (e.key === "Enter" && searchTitle.length > 2) {
            searchTemplatesList()
          }
        }}
        cancelButtonOnClick={() => {
          setSearchTitle("")
          if (hasSearched) {
            setUpdatingList(true)

            if (!searchLang && !searchStage) {
              getTemplatesList(false)
            } else {
              searchTemplatesList()
            }
          }
        }}
        searchButtonOnClick={() => {
          searchTemplatesList()
        }}
        addButtonOnClick={handleDialogChange}
      />
      {loading ? (
        <TableSpinner
          height={
            showPadding === "yes"
              ? "calc(100vh - 208px)"
              : "calc(100vh - 175px)"
          }
        />
      ) : (
        <ListTable
          height={
            showPadding === "yes"
              ? "calc(100vh - 208px)"
              : "calc(100vh - 175px)"
          }
          headingItems={[
            "Stage",
            "Handle & ID",
            "Translations",
            "Status",
            "Type",
            "Format",
            "Updated",
          ]}
          nextToken={templatesListNextToken}
          loadingMoreItems={loadMoreLoading}
          setLoadingMoreItems={setLoadMoreLoading}
          loadMore={
            !searchTitle.length
              ? loadMoreTemplates
              : async () => {
                  const result = await searchTemplatesList(false, true)
                  return result
                }
          }
          tableBody={templatesList.map((item: NotificationTemplate) => (
            <TemplatesListRow
              key={item.id}
              item={item}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
              forDialog={forDialog}
            />
          ))}
        />
      )}
      <CreateTemplateDialog
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        handleDialogChange={handleDialogChange}
      />
    </ListContainer>
  )
}

export default TemplatesList
